import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppPayFrequencySelect from '@nxsys/components/AppPayFrequencySelect';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import { paymentModelOptionsIreland } from '@nxsys/constants/AppConstants';
import {
  defaultsBusinessTypeOptions,
  titleOptions,
} from '@nxsys/constants/prospect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import { validatePhone } from '@nxsys/utils/validator';
import { Form } from 'antd';
import { filter, includes, intersection, isEmpty } from 'lodash';
import moment from 'moment';
import React, { Ref, useImperativeHandle, useMemo } from 'react';
import { useSelector } from 'react-redux';

export type ProspectFormValues = {
  title?: Option<string> | null;
  dob?: string;
  forename?: string;
  surname?: string;
  agency?: Option<string> | null;
  paymentCompany?: Option<string> | null;
  email?: string;
  phoneNumber?: string;
  payFrequency?: string;
  paymentModel?: Option<string> | null;
  businessType?: Option<string> | null;
  payRate?: number;
};

export type ProspectFormRef = {
  submit: () => void;
  reset: () => void;
};

type ProspectFormProps = {
  formRef?: Ref<ProspectFormRef>;
  onSubmit?: (values: ProspectFormValues) => void;
  defaultValues?: ProspectFormValues;
  disabled?: boolean;
};

const ProspectForm: React.FC<ProspectFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
  disabled = false,
}) => {
  const { messages } = useTypedIntl();

  const { isBureau, isAgency, payrollCountry, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  const agencyCompanyRef = isAgency ? user?.agencyContactRef?.companyRef : null;
  const userAgencyId = isAgency ? user?.agencyId?.id : null;
  const userAgencyName = isAgency ? user?.agencyName : null;
  // An agency user may only raise prospects on the models its own agency runs
  const agencyPaymentModels = useMemo(
    () => (isAgency ? (user?.agencyId?.paymentModel ?? []) : []),
    [isAgency, user?.agencyId?.paymentModel],
  );

  // Pre-fill company & agency for agency users via initialValues (not useEffect)
  // so that form.resetFields() restores them after each save
  const agencyDefaults = useMemo(() => {
    const values: Partial<ProspectFormValues> = {};
    if (agencyCompanyRef) {
      values.paymentCompany = {
        value: agencyCompanyRef.id,
        label: agencyCompanyRef.companyName,
        fullData: {
          paymentModel: agencyCompanyRef.paymentModel,
        },
      } as any;
    }
    if (userAgencyId && userAgencyName) {
      values.agency = {
        value: userAgencyId,
        label: userAgencyName,
      } as any;
    }
    return values;
  }, [agencyCompanyRef, userAgencyId, userAgencyName]);

  const [form] = Form.useForm<ProspectFormValues>();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
    reset() {
      form.resetFields();
    },
  }));

  const watchPaymentCompany = Form.useWatch('paymentCompany', form);
  const companyPayment = watchPaymentCompany?.fullData;
  const watchPaymentModel = Form.useWatch('paymentModel', form);
  const paymentModelValue = watchPaymentModel?.value ?? '';

  const appStore = useSelector((state: AppState) => state.appStore);
  // Ireland exposes its own PAYE/RCT list; RCT maps to the BE value CIS
  const defaultPaymentModel = isIreland
    ? paymentModelOptionsIreland
    : appStore?.appConfigs?.paymentModels;
  const paymentModelOptions = useMemo(() => {
    // A company running CIS implicitly runs NON_CIS too — but Ireland has no
    // NON_CIS at all, its CIS value is RCT
    const expandCIS = (models: string[]) =>
      !isIreland && includes(models, 'CIS')
        ? [...models, 'NON_CIS']
        : models;

    const companyModels = expandCIS(companyPayment?.paymentModel ?? []);
    const agencyModels = expandCIS(agencyPaymentModels);

    // Both sides configured -> only what they agree on; otherwise whichever
    // side is set carries the constraint on its own
    const allowed =
      !isEmpty(companyModels) && !isEmpty(agencyModels)
        ? intersection(companyModels, agencyModels)
        : !isEmpty(companyModels)
          ? companyModels
          : agencyModels;

    return !isEmpty(allowed)
      ? filter(defaultPaymentModel, (pm) => includes(allowed, pm.value))
      : [];
  }, [companyPayment, agencyPaymentModels, defaultPaymentModel, isIreland]);

  const formItems: {
    label: string;
    name: keyof ProspectFormValues;
    required?: boolean;
    type: string;
    placeholder?: string;
    rules?: any[];
    options?: any[];
    hidden?: boolean;
    apiType?: string;
    selectAsyncType?: string;
    paramFilter?: Record<string, any>;
    disabled?: boolean;
  }[] = [
    {
      label: messages['common.title'],
      name: 'title',
      required: true,
      type: 'select',
      options: titleOptions,
      placeholder: messages['form.selectTitle'],
    },
    {
      label: messages['common.forename'],
      name: 'forename',
      required: true,
      type: 'text',
      placeholder: messages['form.enterForename'],
    },
    {
      label: messages['common.surname'],
      name: 'surname',
      required: true,
      type: 'text',
      placeholder: messages['form.enterSurname'],
    },
    {
      label: messages['common.dob'],
      name: 'dob',
      type: 'date',
      placeholder: messages['form.selectDate'],
      required: isIreland,
      rules: [
        {
          validator: (_: any, val: any) => {
            if (!val) return Promise.resolve();
            const yearOfDOB = moment(val).year();
            const yearNow = moment().year();
            const age = yearNow - yearOfDOB;
            if (age >= 10 && age <= 100) return Promise.resolve();
            return Promise.reject(
              new Error(messages['validation.dobAgeRange']),
            );
          },
        },
      ],
    },
    {
      label: messages['common.email'],
      name: 'email',
      required: true,
      type: 'text',
      placeholder: messages['form.enterEmail'],
      rules: [
        {
          type: 'email' as const,
          message: messages['form.enterValidEmail'],
        },
      ],
    },
    {
      label: messages['common.phoneNumber'],
      name: 'phoneNumber',
      type: 'phone',
      placeholder: messages['form.enterPhoneNumber'],
      required:
        companyPayment?.autoEnrolSettings?.pensionProviderId ===
        'SMART_PENSION',
      // FE-BUG-009: Add phone validation consistent with CandidateForm/AgencyForm
      rules: [
        {
          validator: (_: any, value: string) => {
            if (!value) return Promise.resolve();
            if (!validatePhone(value)) {
              return Promise.reject(messages['form.enterValidMobile']);
            }
            return Promise.resolve();
          },
        },
      ],
    },
    {
      label: isBureau
        ? messages['common.employer']
        : messages['common.paymentCompany'],
      name: 'paymentCompany',
      type: 'selectAsync',
      apiType: 'searchCompany',
      selectAsyncType: 'company',
      placeholder: messages['form.selectCompany'],
      paramFilter: { subOnboarding: true },
      disabled: !!agencyCompanyRef,
      required: true,
    },
    // An agency user can only ever raise prospects for their own agency, so
    // the field is pointless there — it stays in initialValues so updates keep
    // sending the right agencyId
    ...(!isBureau && !isAgency
      ? [
          {
            label: messages['common.agencyName'],
            name: 'agency' as const,
            type: 'selectAsync',
            apiType: 'searchAgency',
            selectAsyncType: 'agency',
            placeholder: messages['form.selectAgency'],
          },
        ]
      : []),
    {
      label: messages['common.payFrequency'],
      name: 'payFrequency',
      required: true,
      type: 'payFrequencySelect',
      placeholder: messages['form.selectPayFrequency'],
    },
    ...(!isUK
      ? [
          {
            label: messages['common.paymentModel'],
            name: 'paymentModel' as const,
            type: 'select',
            options: paymentModelOptions,
            placeholder: messages['form.selectPaymentModel'],
            required: true,
          },
          // Ireland has no business type — its CIS value is RCT, not
          // self-employment
          ...(!isBureau &&
          !isIreland &&
          ['CIS', 'NON_CIS'].includes(paymentModelValue)
            ? [
                {
                  label: messages['common.businessType'],
                  name: 'businessType' as const,
                  type: 'select',
                  options: defaultsBusinessTypeOptions,
                  placeholder: messages['form.selectBusinessType'],
                },
              ]
            : []),
        ]
      : []),
    ...(!isBureau
      ? [
          {
            label: messages['common.payRate'],
            name: 'payRate' as const,
            type: 'number',
            placeholder: messages['form.enterPayRate'],
          },
        ]
      : []),
  ];

  const onFinish = (values: ProspectFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  // A business type belongs to one payment model, so switching model drops it.
  // Only user edits land here — setFieldsValue does not trigger this.
  const onValuesChange = (changedValues: Partial<ProspectFormValues>) => {
    if ('paymentModel' in changedValues) {
      form.setFieldValue('businessType', null);
    }
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onValuesChange={onValuesChange}
      initialValues={{ ...agencyDefaults, ...defaultValues }}
    >
      {formItems.map((item) => (
        <Form.Item
          key={item.name}
          label={item.label}
          name={item.name}
          rules={[
            {
              required: !!item.required,
              message:
                item.type === 'select' ||
                item.type === 'payFrequencySelect' ||
                item.type === 'selectAsync'
                  ? messages['form.pleaseSelect']
                  : messages['form.pleaseEnter'],
            },
            ...(item.rules ?? []),
          ]}
        >
          {item.type === 'select' ? (
            <AppSelect
              placeholder={item.placeholder}
              options={item.options ?? []}
              disabled={disabled}
            />
          ) : item.type === 'date' ? (
            <AppDatePicker
              placeholder={item.placeholder}
              format={'DD/MM/YYYY'}
              disabled={disabled}
            />
          ) : item.type === 'phone' ? (
            <AppInput
              placeholder={item.placeholder}
              disabled={disabled}
              onChange={(e) => {
                form.setFieldValue(
                  item.name,
                  e.target.value.replace(/\D/g, ''),
                );
              }}
            />
          ) : item.type === 'text' || item.type === 'number' ? (
            <AppInput
              placeholder={item.placeholder}
              type={item.type}
              allowCapitalize={item.name !== 'email'}
              disabled={disabled}
            />
          ) : item.type === 'payFrequencySelect' ? (
            <AppPayFrequencySelect
              placeholder={item.placeholder}
              disabled={disabled}
              onChange={(val: any) =>
                form.setFieldsValue({ payFrequency: val })
              }
            />
          ) : item.type === 'selectAsync' ? (
            <AppSelectSync
              placeholder={item.placeholder}
              apiType={item.apiType as any}
              type={item.selectAsyncType ?? ''}
              onChangeValue={(value: any) => {
                form.setFieldsValue({ [item.name]: value });
                // A different employer allows a different set of payment
                // models, so drop the stale selection and what depends on it
                if (item.name === 'paymentCompany') {
                  form.setFieldsValue({
                    paymentModel: null,
                    businessType: null,
                  });
                }
              }}
              {...(item.paramFilter ? { paramFilter: item.paramFilter } : {})}
              {...(disabled || item.disabled ? { disabled: true } : {})}
            />
          ) : null}
        </Form.Item>
      ))}
    </AppFormContainer>
  );
};

export default ProspectForm;
